// src/hooks/useLiveBroadcast.ts
import { useEffect, useRef, useCallback, useState } from 'react';
import { MatchState, LiveTimer, LiveDecision } from '../types';

/**
 * Résultat d'un set pour l'historique
 */
interface SetResult {
  scoreA: number;
  scoreB: number;
  winner: 'A' | 'B';
  durationMinutes: number;
}

/**
 * Payload envoyé au backend pour le live scoring
 */
interface LiveScorePayload {
  matchId: string;
  context: string;
  centerName: string;
  centerId: string;
  courtName: string;
  courtId: string;
  playerA: string;
  playerB: string;
  playerAColor: string;
  playerBColor: string;
  pointScore: { A: number; B: number };
  gameScore: { A: number; B: number };
  servingPlayer: 'A' | 'B' | null;
  timestamp: number;
  matchEnded?: boolean;
  bestOf: 3 | 5;
  setsHistory: SetResult[];
  sessionId: string;
  timer?: { id: string; durationSeconds: number; startedAt: number; pausedAt?: number | null } | null;
  lastDecision?: { type: string; player: 'A' | 'B'; timestamp: number } | null;
}

export type BroadcastStatus = 'active' | 'inactive' | 'off';

interface UseLiveBroadcastProps {
  matchState: MatchState;
  enabled?: boolean;
}

// Timeout pour les requêtes HTTP (5 secondes)
const REQUEST_TIMEOUT = 5000;

/**
 * Random UUID v4. `crypto.randomUUID()` exists only in secure contexts (https, localhost):
 * on a plain http origin, such as a LAN address, it is undefined and every broadcast used to
 * fail before its request. `crypto.getRandomValues()` is available everywhere.
 */
export function randomUuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Returns a stable session ID for the current browser tab.
 * Generated once per tab, persisted in sessionStorage.
 */
function getSessionId(): string {
  let id = sessionStorage.getItem('liveScoreSessionId');
  if (!id) {
    id = randomUuid();
    sessionStorage.setItem('liveScoreSessionId', id);
  }
  return id;
}

// Délai de debounce pour éviter les rafales (300ms)
const DEBOUNCE_DELAY = 300;

/**
 * Hook pour broadcaster les scores en temps réel vers une SmartTV
 *
 * Envoie silencieusement les mises à jour de score au backend PHP
 * à chaque changement de score, serveur, rollback, ou timer.
 */
export function useLiveBroadcast({ matchState, enabled = true }: UseLiveBroadcastProps): { broadcastStatus: BroadcastStatus } {
  const liveScoreUrl = matchState.matchInfo.liveScoreUrl;

  // Broadcast status: 'off' when disabled, 'active'/'inactive' based on last POST result
  const [broadcastStatus, setBroadcastStatus] = useState<BroadcastStatus>(() =>
    enabled && liveScoreUrl ? 'active' : 'off'
  );

  // Refs pour tracker les changements (avec valeurs par défaut pour éviter les erreurs)
  const prevGameScoresRef = useRef({ A: matchState.gameScores?.A ?? 0, B: matchState.gameScores?.B ?? 0 });
  const prevSetScoresRef = useRef({ A: matchState.setScores?.A ?? 0, B: matchState.setScores?.B ?? 0 });
  const prevServingPlayerRef = useRef(matchState.servingPlayer);
  const prevServingSideRef = useRef(matchState.servingSide);
  const prevHistoryLengthRef = useRef(matchState.history?.length ?? 0);
  const prevScreenRef = useRef(matchState.currentScreen);
  const prevActiveTimerRef = useRef<LiveTimer | null | undefined>(matchState.activeTimer);
  const prevLastDecisionRef = useRef<LiveDecision | null | undefined>(matchState.lastDecision);
  const matchEndedSentRef = useRef(false);

  // Ref pour le debounce
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Ref pour éviter les envois multiples
  const isSendingRef = useRef(false);

  // Sync 'off' status when broadcasting is disabled
  useEffect(() => {
    if (!enabled || !liveScoreUrl) {
      setBroadcastStatus('off');
    }
  }, [enabled, liveScoreUrl]);

  /**
   * Génère le payload à envoyer au backend
   */
  const generatePayload = useCallback((matchEnded: boolean = false): LiveScorePayload => {
    // Convertir gameResults en setsHistory
    const setsHistory: SetResult[] = matchState.gameResults.map(result => ({
      scoreA: result.scoreA,
      scoreB: result.scoreB,
      winner: result.winner,
      durationMinutes: Math.round(result.duration / 60000)
    }));

    const payload: LiveScorePayload = {
      matchId: matchState.matchInfo.matchId,
      context: matchState.matchInfo.context || '',
      centerName: matchState.matchInfo.centerName || '',
      centerId: matchState.matchInfo.centerId || '',
      courtName: matchState.matchInfo.courtName || '',
      courtId: matchState.matchInfo.courtId || '',
      playerA: matchState.matchInfo.playerA,
      playerB: matchState.matchInfo.playerB,
      playerAColor: matchState.playerAColor,
      playerBColor: matchState.playerBColor,
      pointScore: {
        A: (matchState.pendingGameEnd || matchState.matchEnded) ? 0 : matchState.gameScores.A,
        B: (matchState.pendingGameEnd || matchState.matchEnded) ? 0 : matchState.gameScores.B
      },
      gameScore: {
        A: matchState.setScores.A,
        B: matchState.setScores.B
      },
      servingPlayer: matchState.servingPlayer,
      timestamp: Date.now(),
      bestOf: matchState.matchInfo.scoringType === 'BestOf3' ? 3 : 5,
      setsHistory,
      sessionId: getSessionId(),
      timer: matchState.activeTimer || null,
      lastDecision: matchState.lastDecision || null
    };

    if (matchEnded || matchState.matchEnded) {
      payload.matchEnded = true;
    }

    return payload;
  }, [
    matchState.matchInfo.matchId,
    matchState.matchInfo.context,
    matchState.matchInfo.centerName,
    matchState.matchInfo.centerId,
    matchState.matchInfo.courtName,
    matchState.matchInfo.courtId,
    matchState.matchInfo.playerA,
    matchState.matchInfo.playerB,
    matchState.matchInfo.scoringType,
    matchState.playerAColor,
    matchState.playerBColor,
    matchState.gameScores.A,
    matchState.gameScores.B,
    matchState.setScores.A,
    matchState.setScores.B,
    matchState.servingPlayer,
    matchState.gameResults,
    matchState.activeTimer,
    matchState.lastDecision,
    matchState.pendingGameEnd,
    matchState.matchEnded
  ]);

  /**
   * Envoie les données au backend (silencieux)
   */
  const sendLiveScore = useCallback(async (matchEnded: boolean = false) => {
    if (!liveScoreUrl || isSendingRef.current) {
      return;
    }

    isSendingRef.current = true;

    try {
      const payload = generatePayload(matchEnded);

      // Créer un AbortController pour le timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await fetch(liveScoreUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        setBroadcastStatus('active');
      } else {
        console.warn('[LiveBroadcast] Server returned error:', response.status);
        setBroadcastStatus('inactive');
      }

    } catch (error) {
      // Erreurs silencieuses - on log juste pour debug
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn('[LiveBroadcast] Request timeout');
      } else {
        console.warn('[LiveBroadcast] Send failed:', error);
      }
      setBroadcastStatus('inactive');
    } finally {
      isSendingRef.current = false;
    }
  }, [liveScoreUrl, generatePayload]);

  /**
   * Envoie avec debounce pour éviter les rafales
   */
  const debouncedSend = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      sendLiveScore();
    }, DEBOUNCE_DELAY);
  }, [sendLiveScore]);

  /**
   * Effet principal - surveille les changements
   */
  useEffect(() => {
    // Vérifier si le broadcast est activé
    if (!enabled || !liveScoreUrl) {
      return;
    }

    // Vérifier si centerId et courtId sont présents (requis pour le backend)
    if (!matchState.matchInfo.centerId || !matchState.matchInfo.courtId) {
      console.warn('[LiveBroadcast] Missing centerId or courtId - broadcast disabled');
      return;
    }

    // Détecter les changements
    const gameScoresChanged =
      prevGameScoresRef.current.A !== matchState.gameScores.A ||
      prevGameScoresRef.current.B !== matchState.gameScores.B;

    const setScoresChanged =
      prevSetScoresRef.current.A !== matchState.setScores.A ||
      prevSetScoresRef.current.B !== matchState.setScores.B;

    const servingPlayerChanged = prevServingPlayerRef.current !== matchState.servingPlayer;
    const servingSideChanged = prevServingSideRef.current !== matchState.servingSide;

    // Détecter un rollback (history length diminue)
    const historyLengthDecreased = matchState.history.length < prevHistoryLengthRef.current;

    // Détecter changement de timer actif
    const activeTimerChanged = prevActiveTimerRef.current !== matchState.activeTimer;

    // Détecter changement de dernière décision
    const lastDecisionChanged = prevLastDecisionRef.current !== matchState.lastDecision;

    // Détecter la transition vers l'écran de fin de match
    const matchJustEnded =
      prevScreenRef.current !== 'after' &&
      matchState.currentScreen === 'after' &&
      !matchEndedSentRef.current;

    // Mettre à jour les refs
    prevGameScoresRef.current = { A: matchState.gameScores.A, B: matchState.gameScores.B };
    prevSetScoresRef.current = { A: matchState.setScores.A, B: matchState.setScores.B };
    prevServingPlayerRef.current = matchState.servingPlayer;
    prevServingSideRef.current = matchState.servingSide;
    prevHistoryLengthRef.current = matchState.history.length;
    prevScreenRef.current = matchState.currentScreen;
    prevActiveTimerRef.current = matchState.activeTimer;
    prevLastDecisionRef.current = matchState.lastDecision;

    // Si le match vient de se terminer, envoyer immédiatement avec matchEnded=true
    if (matchJustEnded) {
      matchEndedSentRef.current = true;
      // Annuler tout debounce en cours
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      sendLiveScore(true);
      return;
    }

    // Envoyer si quelque chose a changé
    if (gameScoresChanged || setScoresChanged || servingPlayerChanged || servingSideChanged || historyLengthDecreased || activeTimerChanged || lastDecisionChanged) {
      debouncedSend();
    }
  }, [
    enabled,
    liveScoreUrl,
    matchState.matchInfo.centerId,
    matchState.matchInfo.courtId,
    matchState.gameScores.A,
    matchState.gameScores.B,
    matchState.setScores.A,
    matchState.setScores.B,
    matchState.servingPlayer,
    matchState.servingSide,
    matchState.history.length,
    matchState.currentScreen,
    matchState.activeTimer,
    matchState.lastDecision,
    debouncedSend,
    sendLiveScore
  ]);

  // Cleanup du timer au démontage
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { broadcastStatus };
}
